(() => {
  const root = document.querySelector('[data-collection-page]');
  if (!root) return;

  // Avoid double-binding if this script is included more than once.
  if (root.dataset.collectionFiltersInitialized === 'true') return;
  root.dataset.collectionFiltersInitialized = 'true';

  const toggleBtn = root.querySelector('[data-collection-filters-toggle]');
  const layout = root.querySelector('[data-collection-layout]');
  const desktopPanel = root.querySelector('[data-collection-filters-desktop]');
  const grid = root.querySelector('[data-collection-grid]');
  const dialog = root.querySelector('[data-collection-filters-dialog]');
  const closeBtn = root.querySelector('[data-collection-filters-close]');

  const setDesktopOpen = (isOpen) => {
    if (!desktopPanel) return;

    const toggleClasses = (el, classes, on) => {
      classes.forEach((cls) => el.classList.toggle(cls, on));
    };

    const applyState = (el, openClasses, closedClasses) => {
      toggleClasses(el, openClasses, isOpen);
      toggleClasses(el, closedClasses, !isOpen);
    };

    applyState(
      desktopPanel,
      ['w-80', 'xl:w-auto', 'opacity-100', 'pointer-events-auto', 'border-border', 'translate-x-0'],
      ['w-0', 'opacity-0', 'pointer-events-none', 'border-transparent', '-translate-x-full']
    );

    toggleBtn?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (layout) {
      toggleClasses(layout, ['xl:grid', 'xl:grid-cols-[1fr_auto]'], isOpen);
    }

    if (grid) {
      applyState(
        grid,
        ['lg:grid-cols-2', 'xl:grid-cols-[repeat(2,480px)]', 'xl:justify-start'],
        ['lg:grid-cols-3', 'xl:justify-center']
      );
    }
  };

  const openMobile = () => {
    if (!dialog) return;
    delete dialog.dataset.status;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    window.requestAnimationFrame(() => {
      dialog.dataset.status = 'open';
    });
  };

  const closeMobile = () => {
    if (!dialog) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let didFinish = false;
    const finish = () => {
      if (didFinish) return;
      didFinish = true;
      dialog.removeEventListener('transitionend', finish);
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      delete dialog.dataset.status;
    };

    if (prefersReducedMotion) {
      finish();
      return;
    }

    delete dialog.dataset.status;
    dialog.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 450);
  };

  toggleBtn?.addEventListener('click', () => {
    if (window.matchMedia('(min-width: 64rem)').matches) {
      const isOpen = desktopPanel && desktopPanel.classList.contains('w-80');
      setDesktopOpen(!isOpen);
      return;
    }

    openMobile();
  });

  closeBtn?.addEventListener('click', () => closeMobile());

  dialog?.addEventListener('click', (e) => {
    if (e.target === dialog) closeMobile();
  });

  dialog?.addEventListener('cancel', (e) => {
    e.preventDefault();
    closeMobile();
  });

  dialog?.addEventListener('close', () => {
    delete dialog.dataset.status;
  });

  /**
   * Build clean URL from form data and current URL params
   * Preserves existing filters, updates sort, removes empty values
   */
  const buildFilterUrl = (form, sortValue = null) => {
    const currentParams = new URLSearchParams(window.location.search);
    const formData = form ? new FormData(form) : null;
    const params = new URLSearchParams();

    // Set sort_by (from parameter, form, or current URL - in that priority)
    const sort =
      sortValue || (formData ? formData.get('sort_by') : null) || currentParams.get('sort_by');
    if (sort) {
      params.set('sort_by', sort);
    }

    // Add all filter parameters from form (excluding empty values and sort_by)
    if (formData) {
      for (const [key, value] of formData.entries()) {
        if (key === 'sort_by') continue;
        const strValue = String(value).trim();
        if (strValue !== '') {
          // Convert price filter values from dollars to cents (Shopify stores prices in cents)
          if (key.includes('filter.v.price')) {
            const numValue = parseFloat(strValue);
            if (!isNaN(numValue)) {
              params.append(key, String(Math.round(numValue * 100)));
            }
          } else {
            params.append(key, value);
          }
        }
      }
    }

    // Preserve any existing URL params not in form (fallback for edge cases)
    for (const [key, value] of currentParams.entries()) {
      if (key === 'sort_by') continue;
      // Skip if already added from form (form takes precedence)
      if (formData && formData.has(key)) continue;
      const strValue = String(value).trim();
      if (strValue !== '') {
        params.append(key, value);
      }
    }

    // B2C: ensure B2B filter (No) is included so B2B-only products stay hidden
    const isB2C =
      !window.location.hostname.includes('wholesale.') &&
      document.documentElement.getAttribute('data-theme') !== 'b2b';
    if (isB2C) {
      const configEl = document.getElementById('b2c-filter-config');
      let b2cParam = 'filter.p.m.custom.b2b_only_product';
      let b2cValue = 'No';
      if (configEl && configEl.textContent) {
        try {
          const cfg = JSON.parse(configEl.textContent.trim());
          if (cfg.param && cfg.value) {
            b2cParam = cfg.param;
            b2cValue = cfg.value;
          }
        } catch (_) {}
      }
      if (params.get(b2cParam) !== b2cValue) {
        params.set(b2cParam, b2cValue);
      }
    }

    const baseUrl = form
      ? form.getAttribute('action') || window.location.pathname
      : window.location.pathname;
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  /**
   * Show loading states
   */
  const showLoading = () => {
    const filterButton = root.querySelector('[data-collection-filters-toggle]');
    if (filterButton) filterButton.loading = true;

    const productsContainer = root.querySelector('[data-collection-products]');
    if (productsContainer) {
      productsContainer.style.opacity = '0.5';
      productsContainer.style.pointerEvents = 'none';
    }
  };

  /**
   * Navigate to filter URL
   */
  const navigateToFilters = (url) => {
    window.location.href = url;
  };

  // Handle sort change
  // Use event delegation to catch change events from both native selects and ck-select's internal selects
  root.addEventListener('change', (e) => {
    const target = e.target;

    // Check if change event is from a sort select (either native select or ck-select's internal select)
    const selectHost =
      target.closest('[data-collection-sort-select]') ||
      (target.tagName === 'SELECT' && target.hasAttribute('data-collection-sort-select')
        ? target
        : null);

    if (!selectHost) return;

    e.preventDefault();
    e.stopPropagation();

    // Get the value from the actual select element that changed
    const sortValue = target.value;
    if (!sortValue) return;

    const formId = selectHost.getAttribute('form');
    const form = formId ? document.getElementById(formId) : selectHost.closest('form');
    if (!form) return;

    showLoading();
    const url = buildFilterUrl(form, sortValue);
    navigateToFilters(url);
  });

  // Handle filter form submission (Apply button)
  // Support both collection and search filter forms
  const filterForm =
    document.getElementById('collection-filters-form') ||
    document.getElementById('search-filters-form');
  if (filterForm) {
    filterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();

      showLoading();

      // Set loading on Apply buttons
      const formId = filterForm.id;
      const applyButtons = root.querySelectorAll(
        `button[type="submit"][form="${formId}"], ck-button[type="submit"][form="${formId}"]`
      );
      applyButtons.forEach((btn) => {
        if (btn.tagName === 'CK-BUTTON') {
          btn.loading = true;
        } else {
          const spinner = document.createElement('ck-spinner');
          spinner.setAttribute('size', 'sm');
          spinner.className = 'mr-2';
          if (btn.firstChild) {
            btn.insertBefore(spinner, btn.firstChild);
          } else {
            btn.appendChild(spinner);
          }
        }
      });

      const url = buildFilterUrl(filterForm);
      navigateToFilters(url);
    });
  }

  // Ensure initial UI state matches markup.
  setDesktopOpen(false);

  /**
   * Handle "Load more" pagination via AJAX
   * Prevents page reload and maintains scroll position
   */
  const handleLoadMore = async (e) => {
    const loadMoreLink = e.target.closest('[data-load-more]');
    if (!loadMoreLink) return;

    e.preventDefault();
    e.stopPropagation();

    const nextUrl = loadMoreLink.getAttribute('href');
    if (!nextUrl) return;

    const grid = root.querySelector('[data-collection-grid]');
    const loadMoreContainer = root.querySelector('[data-load-more-container]');
    if (!grid || !loadMoreContainer) return;

    // Show loading state
    const originalText = loadMoreLink.textContent;
    loadMoreLink.style.opacity = '0.5';
    loadMoreLink.style.pointerEvents = 'none';
    loadMoreLink.textContent = 'Loading...';

    try {
      // Fetch the next page
      const response = await fetch(nextUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the grid and load more button in the fetched HTML
      // Both collection and search pages use [data-collection-page]
      const fetchedRoot = doc.querySelector('[data-collection-page]');
      if (!fetchedRoot) {
        throw new Error('Could not find collection page in response');
      }

      const fetchedGrid = fetchedRoot.querySelector('[data-collection-grid]');
      const fetchedLoadMoreContainer = fetchedRoot.querySelector('[data-load-more-container]');

      if (!fetchedGrid) {
        throw new Error('Could not find product grid in response');
      }

      // Extract new product items (these are already filtered - only displayed products are in the grid)
      const newProducts = Array.from(fetchedGrid.children);
      const newProductsCount = newProducts.length;

      // Append new products to existing grid
      newProducts.forEach((product) => {
        grid.appendChild(product.cloneNode(true));
      });

      // Update item count in header (count only displayed products that were added)
      const countValueEl = document.querySelector('[data-collection-count-value]');
      if (countValueEl && newProductsCount > 0) {
        const currentCount = parseInt(countValueEl.textContent) || 0;
        countValueEl.textContent = currentCount + newProductsCount;
      }

      // Update or remove the "Load more" button
      if (fetchedLoadMoreContainer) {
        const newLoadMoreLink = fetchedLoadMoreContainer.querySelector('[data-load-more]');
        if (newLoadMoreLink) {
          // Update existing button with new URL and text
          loadMoreLink.setAttribute('href', newLoadMoreLink.getAttribute('href'));
          loadMoreLink.textContent = newLoadMoreLink.textContent;
          loadMoreLink.style.opacity = '1';
          loadMoreLink.style.pointerEvents = 'auto';
        } else {
          // No more pages, remove the button
          loadMoreContainer.remove();
        }
      } else {
        // No load more container means no more pages
        loadMoreContainer.remove();
      }

      // Update URL without reloading (for browser history)
      window.history.pushState({}, '', nextUrl);
    } catch (error) {
      console.error('Error loading more products:', error);
      // Restore button state on error
      loadMoreLink.textContent = originalText;
      loadMoreLink.style.opacity = '1';
      loadMoreLink.style.pointerEvents = 'auto';
      // Fallback to regular navigation
      window.location.href = nextUrl;
    }
  };

  // Handle "Load more" button clicks
  root.addEventListener('click', handleLoadMore);
})();
